<script setup lang="ts">
import { onMounted, useTemplateRef, watch } from 'vue';
import type { Option } from '@/modules/common/types/option.type.ts';

const model = defineModel<string | number>({ required: true });

const { size = 'm' } = defineProps<{
  options: Option[];
  size?: 's' | 'm';
  disabled?: boolean;
  fullWidth?: boolean;
}>();

const rootRef = useTemplateRef('rootRef');

function scrollToActive() {
  const root = rootRef.value;
  const active = root?.querySelector<HTMLElement>('.button._active');

  if (!root || !active || root.scrollWidth <= root.clientWidth) {
    return;
  }

  const rootRect = root.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();
  const left = activeRect.left - rootRect.left - root.clientLeft + root.scrollLeft;
  const right = left + activeRect.width;

  if (left < root.scrollLeft) {
    root.scrollLeft = left;
  } else if (right > root.scrollLeft + root.clientWidth) {
    root.scrollLeft = right - root.clientWidth;
  }
}

watch(model, scrollToActive, { flush: 'post' });

onMounted(scrollToActive);
</script>

<template>
  <div
    ref="rootRef"
    class="ui-button-toggle"
    :class="[`_size-${size}`, { '_disabled': disabled, '_full-width': fullWidth }]"
  >
    <button
      v-for="option in options"
      :key="option.code"
      tabindex="-1"
      class="button"
      :class="[{ _active: option.code === model }]"
      :disabled="disabled"
      @click="model = option.code"
    >
      {{ $maybeT(option.label) }}
    </button>
  </div>
</template>

<style scoped lang="scss">
.ui-button-toggle {
  display: flex;
  width: min-content;
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  background-color: var(--color-gray-600);
  transition: opacity var(--transition-fast);

  &._size-s {
    padding: 4px;
    gap: 4px;
    border-radius: 4px;

    .button {
      padding: 0 4px;
      border-radius: 2px;
    }
  }

  &._size-m {
    padding: 4px;
    gap: 4px;
    border-radius: 8px;

    .button {
      padding: 4px 8px;
      border-radius: 6px;
    }
  }

  &._disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &._full-width {
    width: 100%;

    .button {
      flex: 1 1 auto;
    }
  }

  @media (hover: none) {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.button {
  flex-shrink: 0;
  white-space: nowrap;
  color: var(--color-white);
  font-size: var(--font-size);
  line-height: var(--line-height);
  background-color: var(--color-gray-600);
  transition: background-color var(--transition-fast);

  &:disabled {
    cursor: not-allowed;
  }

  &._active {
    cursor: default;
    color: var(--color-gray-1000);
    background-color: var(--color-white);
  }

  @media (hover: hover) {
    &:not(:disabled, ._active) {
      &:hover {
        background-color: var(--color-gray-800);
      }
    }
  }
}
</style>
